/** @format */
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import PaperedTable from '../../components/Table/PaperedTable';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import TableCell from '@material-ui/core/TableCell';
import { TableBody, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import { SimCardServiceTypeCollection } from '../../interfaces/collections/SimCardServiceInterface';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import ServicesListItem from './ServicesListItem';
import Pager from '../../components/Adescom/PagerRange';
import { history } from '../../core/history';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import FilterList from '../../components/Filter/FilterList';
import { FilterReducerFieldName, FilterReducers } from '../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { FilterManager } from '../../modules/filter/FilterManager';
import { FeatureTypes } from '../../modules/features/FeaturesTypes';
import { FeaturesServiceReducerType, ServicesAction } from '../../modules/features/FeaturesReducers';
import { useSimCardServiceGroupHeader, useSimCardServiceGroupList } from '../../modules/lib/hooks/useSimCardServiceGroupList';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import { HeaderCheckBoxSelectListElement } from '../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { SimCardServiceTypeAddForm } from './SimCardServiceTypeAddForm';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { ServiceListToCsv } from './csv/ServiceListToCsv';

const _ = require('underscore');

type ServiceListType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const setDefaultFilters = (): Array<Filter> => [{ name: 'systemComponent', value: false }];
const setDefaultOrders = (): Array<Order> => [{ name: 'description', value: 'asc' }];

const ServiceList: FunctionComponent<ServiceListType> = ({ classes, showMainLoader }): React.ReactElement => {
    const dispatch = useDispatch();
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const { type, serviceList } = useSelector((state: { serviceReducer: FeaturesServiceReducerType }) => state.serviceReducer);

    const [addDialog, setAddDialog] = useState(false);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [activeRow, setActiveRow] = useState(-1);
    const [reload, setReload] = useState(false);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const [groupList, setGroupList] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [listTypePrepared, setListTypePrepared] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const listType = new FeatureTypes(process.env);

    const processFilters = useCallback(
        (filterProcess: Array<Filter>): Array<Filter> => {
            const newFilters: Array<Filter> = [];
            for (let i = 0; i < filterProcess.length; i++) {
                const value = filterProcess[i].value;
                if (Array.isArray(value)) {
                    for (let j = 0; j < value.length; j++) {
                        const valueT = value[j];
                        if (filterProcess[i].name === 'serviceType') {
                            if (typeof valueT === 'number') {
                                newFilters.push({
                                    name: filterProcess[i].name + '[]',
                                    value: listType.mapToString(valueT),
                                });
                            }
                        } else {
                            newFilters.push({ name: filterProcess[i].name + '[]', value: valueT });
                        }
                    }
                } else {
                    newFilters.push(filterProcess[i]);
                }
            }
            for (let i = 0; i < listType.operatorType.length; i++) {
                newFilters.push({ name: 'operatorType[]', value: listType.operatorType[i] });
            }
            return newFilters;
        },
        [listType]
    );

    useEffect(() => {
        if (reload) {
            setReload(false);
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types')),
                'sim_card_services_types_list_load',
                processFilters(filters),
                orders,
                (data: SimCardServiceTypeCollection) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    dispatch({
                        type: ServicesAction.FEATURES_LOAD_SIM_CARD_SERVICE_TYPE_LIST,
                        serviceList: data,
                    } as FeaturesServiceReducerType);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, filters, orders, reload, showMainLoader, listType, processFilters]);

    useEffect(() => {
        setReload(true);
    }, [orders]);

    const simCardServiceGroupList = useSimCardServiceGroupList(listType.operatorType);
    const grList = useSimCardServiceGroupHeader(simCardServiceGroupList);

    useEffect(() => {
        if (grList && Array.isArray(grList) && grList.length > 0 && !groupList) {
            setGroupList(grList);
        }
    }, [groupList, listType, grList]);

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    useEffect(() => {
        if (!listTypePrepared) {
            setListTypePrepared(listType.getList());
        }
    }, [listType, listTypePrepared]);

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'sim_card_services_types_list_load',
            (data: SimCardServiceTypeCollection): void => {
                dispatch({
                    type: ServicesAction.FEATURES_LOAD_SIM_CARD_SERVICE_TYPE_LIST,
                    serviceList: data,
                } as FeaturesServiceReducerType);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };
    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const viewCallback = (iri: { uri: string }, event: any): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl('/service-type-edit' + iri.uri));
    };

    if (!groupList && !grList) {
        return <div></div>;
    }


    const exportToCsv = (): void => {
        const items = serviceList;
        if (items && !items['hydra:totalItems']) {
            NotificationHelper.notifyError('general:common.no_data_to_export', dispatch);
            return;
        }
        if (items) {
            const toCsv = new ServiceListToCsv(new ItemLoaders(dispatch));
            toCsv.exportToCsv(orders,filters)
        }
    };
    const viewServiceListManager = (iri: { uri: string }, event: any): void => pushCallback('/services-type-list-manage', event);
  const  pushCallback = (uri: string, event: any): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl(uri));
    };
    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={(): void => {
                                        showMainLoader(true);
                                        setReload(true);
                                    }}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {UserRoleHelper.checkRole(roles.ROLE_UPLOAD_SERVICE_LIST) && (
                                    <CustomTextButtom
                                        callback={viewServiceListManager}
                                        callbackParam={{ uri: 'no-uri' }}
                                        color="primary"
                                        label="prov:features.service_list_manage"
                                        size="lg"
                                    />
                                )}
                                {UserRoleHelper.checkRole(roles.ROLE_UPLOAD_SERVICE_LIST) && (
                                    <CustomTextButtom
                                        callback={exportToCsv}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.export_to_csv"
                                        size="lg"
                                    />
                                )}
                                {showClearFilterButton && (
                                    <CustomTextButtom
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                                {UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TYPE_COMPONENT_POST) && (
                                    <CustomTextButtom
                                        callback={(): void => setAddDialog(true)}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.add_new"
                                        size="lg"
                                    />
                                )}
                                <FilterList />
                            </GridItem>
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.service_name"
                                    textAlign="left"
                                    fieldName="mainname"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.group"
                                    textAlign="left"
                                    fieldName="groupId"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={groupList}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.description"
                                    textAlign="left"
                                    fieldName="description"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.comment"
                                    textAlign="left"
                                    fieldName="comment"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:features.available"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="available"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.serviceType"
                                    textAlign="left"
                                    fieldName="serviceType"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={listTypePrepared}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {serviceList &&
                            serviceList['hydra:member'].map((listItem) => {
                                return (
                                    <ServicesListItem
                                        key={'scst_' + listItem.id}
                                        item={listItem}
                                        activeRow={activeRow}
                                        setActiveRow={setActiveRow}
                                        viewCallback={viewCallback}
                                        simCardServiceGroupList={simCardServiceGroupList}
                                    />
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {serviceList && serviceList['hydra:view'] && <Pager callback={paginationCallbackFunction} views={serviceList} />}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
                <SimCardServiceTypeAddForm
                    serviceGroupList={simCardServiceGroupList}
                    close={(): void => setAddDialog(false)}
                    reload={(): void => setReload(true)}
                    open={addDialog}
                />
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(ServiceList);
